/**
 * The web binding for the registry's `types:` block (`Shared/Analytics/mixpanel_structure.yaml`):
 * each value type's parameter type and the `format:` rule that turns it into its wire value. Fixed,
 * hand-maintained code, like `AnalyticsCodegenBase.kt` on Android and `AnalyticsFormat.swift` on
 * iOS; the generated events call these through `analyticsTypeWires`, whose annotation pins each
 * return type to the registry `wire`.
 */

import type {Id} from 'notability-services-common-universal'

export type NoteIdValue = string
export type UserIdValue = string
/** Seconds, as iOS's `TimeInterval`. */
export type DurationSecondsValue = number
/** Seconds, as iOS's `TimeInterval`; the wire value is whole microseconds. */
export type DurationMicrosecondsValue = number
/** `#rrggbb` in either case, as iOS. */
export type HexColorValue = string
/** An op's flatbuffer `Id`, or anything else that reads out its site and timestamp the same way. */
export type VerboseOpIdValue = Pick<Id, 'site' | 'timestamp'>
export type CommentOpIdValue = Pick<Id, 'site' | 'timestamp'>
/** `null` when there is no op, which the wire sends as -1. */
export type LamportTimestampValue = number | null

export const analyticsFormat = {
  noteId(value: NoteIdValue): string {
    return value.toUpperCase()
  },
  userId(value: UserIdValue): string {
    return value.toLowerCase()
  },
  durationSeconds(value: DurationSecondsValue): number {
    return value
  },
  durationMicroseconds(value: DurationMicrosecondsValue): number {
    return roundHalfAwayFromZero(value * 1_000_000)
  },
  hexColor(value: HexColorValue): string {
    return value.toUpperCase()
  },
  verboseOpId(value: VerboseOpIdValue): string {
    return `site: ${value.site()} timestamp: ${value.timestamp()}`
  },
  commentOpId(value: CommentOpIdValue): string {
    return `${value.site()}-${value.timestamp()}`
  },
  lamportTimestamp(value: LamportTimestampValue): number {
    return value ?? -1
  }
}

// The registry format rule is half away from zero; Math.round sends a negative half toward +Infinity.
function roundHalfAwayFromZero(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value))
}
